import React, { useCallback, useEffect, useState } from 'react';
import { buildLoginUrl } from '../services/auth-client';
import { authConfig } from '../config';

/**
 * Name of the federated IdP in Cognito (set during configuration).
 * This must match the PROVIDER_NAME constant in cognito-federation.ts.
 */
const FEDERATED_PROVIDER_NAME = 'CoreFlowIdP';

interface SystemStatus {
  // 'unknown' means we could not determine the system mode (fetch failed or
  // returned an unexpected shape). We must NOT treat this as bootstrap, or a
  // transient blip would bounce configured users straight to the Cognito
  // Hosted UI instead of the branded login page.
  mode: 'bootstrap' | 'configured' | 'unknown';
  configured: boolean;
  idpName?: string;
}

/**
 * Fetches the public system status (unauthenticated) to determine whether
 * the system is in bootstrap or configured mode.
 *
 * On any failure (network error, non-OK response, unexpected body) this returns
 * mode 'unknown' rather than assuming bootstrap. The caller only auto-forwards
 * to Cognito on a CONFIRMED bootstrap response; every other case renders the
 * branded login page. This fails toward the branded page, never toward the raw
 * Cognito portal.
 */
async function fetchSystemStatus(): Promise<SystemStatus> {
  const apiUrl = (process.env.APP_API_URL ?? '').replace(/\/$/, '');
  try {
    const response = await fetch(`${apiUrl}/v1/admin/settings`);
    if (!response.ok) {
      return { mode: 'unknown', configured: false };
    }
    const json = await response.json();
    const data = json.data ?? json;
    if (data.mode !== 'bootstrap' && data.mode !== 'configured') {
      return { mode: 'unknown', configured: Boolean(data.configured) };
    }
    return {
      mode: data.mode,
      configured: Boolean(data.configured),
      idpName: data.idpName,
    };
  } catch {
    return { mode: 'unknown', configured: false };
  }
}

// ---------------------------------------------------------------------------
// Feature highlights for the marketing panel
// ---------------------------------------------------------------------------

const FEATURES = [
  {
    icon: KanbanIcon,
    title: 'Boards, Pages & Planning',
    description: 'Project boards, wiki pages, and calendar views — everything Jira and Confluence do, unified in one tool.',
  },
  {
    icon: LinkIcon,
    title: 'AI-Powered Knowledge Graph',
    description: 'Every page is automatically decomposed into concepts and linked to related content — your team\'s institutional memory.',
  },
  {
    icon: TeamIcon,
    title: 'Sprint Velocity Built In',
    description: 'Committed, completed, carried over — tracked automatically via swimlane dates. No plugins, no separate tools.',
  },
  {
    icon: ShieldIcon,
    title: 'Your Cloud, Your Rules',
    description: 'Single-tenant deployment in your own account. No shared infrastructure, no vendor-held data. Designed for restricted networks.',
  },
];

// ---------------------------------------------------------------------------
// LoginPage component
// ---------------------------------------------------------------------------

export function LoginPage(): React.ReactElement {
  const isConfigured = authConfig.cognitoDomain && authConfig.clientId && authConfig.redirectUri;
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      return;
    }

    fetchSystemStatus().then((result) => {
      setStatus(result);
      setLoading(false);

      // Only auto-forward to the Cognito Hosted UI during genuine first-run
      // bootstrap (confirmed by a successful status response). For configured
      // systems — and for any indeterminate/failed status — render the branded
      // login page instead of bouncing the user to the raw Cognito portal.
      if (result.mode === 'bootstrap') {
        window.location.href = buildLoginUrl(authConfig);
      }
    });
  }, [isConfigured]);

  const handleFederatedLogin = useCallback((): void => {
    window.location.href = buildLoginUrl(authConfig, FEDERATED_PROVIDER_NAME);
  }, []);

  if (!isConfigured) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background" role="main">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">Configuration Error</h2>
            <p className="text-sm text-muted-foreground">
              Authentication is not configured. Please contact your administrator.
            </p>
          </div>
        </div>
      </main>
    );
  }

  // Loading / bootstrap redirect state.
  // Show the spinner while fetching, or while a confirmed bootstrap redirect is
  // in flight. For 'configured' and 'unknown' we fall through to the branded
  // page — never leaving the user stuck on a spinner or bounced to Cognito.
  if (loading || !status || status.mode === 'bootstrap') {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background" role="main" aria-busy="true">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent mx-auto" aria-hidden="true" />
          <p className="mt-4 text-muted-foreground text-sm">
            {loading ? 'Loading...' : 'Redirecting to login...'}
          </p>
        </div>
      </main>
    );
  }

  // Configured mode — branded marketing login page with product sections
  return (
    <>
    <main className="min-h-screen flex" role="main">
      {/* Left panel — marketing / hero */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden bg-gradient-to-br from-secondary via-secondary/95 to-accent">
        {/* Decorative background shapes */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-primary/40" />
          <div className="absolute bottom-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/60" />
          <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full bg-primary/20" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Top — brand */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <CoreFlowLogo className="h-9 w-9 text-primary-foreground" />
              <span className="text-2xl font-bold text-primary-foreground tracking-tight">
                CoreFlow
              </span>
            </div>
            <p className="text-sm text-primary-foreground/70">
              <a href="https://www.devtechnology.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary-foreground/90 transition-colors">
                A Core Solution by Dev Technology Group
              </a>
            </p>
          </div>

          {/* Middle — hero text */}
          <div className="max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold text-primary-foreground leading-tight mb-6">
              One tool for
              <br />
              <span className="text-primary">boards, docs,</span>
              <br />
              and delivery.
            </h1>
            <p className="text-lg text-primary-foreground/80 leading-relaxed mb-10">
              CoreFlow replaces your Jira + Confluence stack with a single, AI-powered platform.
              Plan on boards, document in pages, track sprint velocity, get notified in Teams —
              all inside your security boundary.
            </p>

            {/* Feature grid */}
            <div className="grid grid-cols-2 gap-4">
              {FEATURES.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-lg bg-primary-foreground/5 border border-primary-foreground/10 p-4 backdrop-blur-sm"
                >
                  <feature.icon className="h-5 w-5 text-primary mb-2" />
                  <h3 className="text-sm font-semibold text-primary-foreground mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-primary-foreground/60 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom — footer */}
          <div className="text-xs text-primary-foreground/50">
            <a href="https://www.devtechnology.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary-foreground/80 transition-colors">
              A Core Solution by Dev Technology Group
            </a>
          </div>
        </div>
      </div>

      {/* Right panel — sign-in form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile logo (visible on small screens where left panel is hidden) */}
          <div className="lg:hidden text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CoreFlowLogo className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-foreground tracking-tight">CoreFlow</span>
            </div>
            <p className="text-sm text-muted-foreground">by Dev Technology Group</p>
          </div>

          {/* Welcome text */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              Welcome back
            </h2>
            <p className="text-sm text-muted-foreground">
              Sign in to continue to your workspace
            </p>
          </div>

          {/* Sign-in card */}
          <div className="rounded-xl border bg-card p-8 shadow-sm">
            <button
              type="button"
              onClick={handleFederatedLogin}
              className="w-full flex items-center justify-center gap-3 rounded-lg bg-primary px-5 py-3.5 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-all hover:shadow-lg active:scale-[0.98]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              Sign in with Dev Technology
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-3 text-muted-foreground">Secure SSO</span>
              </div>
            </div>

            <p className="text-center text-xs text-muted-foreground leading-relaxed">
              Your organization's identity provider handles authentication.
              If you have an active session, you'll be signed in automatically.
            </p>
          </div>

          {/* Trust indicators */}
          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <LockIcon className="h-3.5 w-3.5" />
              Encrypted
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheckIcon className="h-3.5 w-3.5" />
              Secure SSO
            </span>
          </div>
        </div>
      </div>
    </main>

    {/* Product sections — scrollable below the fold */}
    <ProductSections />
    </>
  );
}

// ---------------------------------------------------------------------------
// Product Sections — scrollable content below the login fold
// ---------------------------------------------------------------------------

function ProductSections(): React.ReactElement {
  return (
    <div className="bg-background">
      {/* What Makes CoreFlow Different */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-foreground text-center mb-4">
          What Makes CoreFlow Different?
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          Not another project management SaaS. CoreFlow is a single-tenant, AI-powered platform
          deployed within your own cloud account — your data never leaves your security boundary.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          <DifferentiatorCard
            title="AI That Stays Inside"
            description="Pages are automatically organized into a knowledge graph — concepts extracted, relationships mapped, summaries generated. Your team's institutional memory builds itself and never leaves your environment."
            highlight="Zero data exfiltration"
          />
          <DifferentiatorCard
            title="Jira + Confluence, Unified"
            description="Boards with swimlanes, sprint velocity, and WIP limits. Pages with revision history, wiki-links, and templates. One tool, one login — not multiple subscriptions adding up per seat."
            highlight="Reduce tool sprawl"
          />
          <DifferentiatorCard
            title="Your Cloud, Your Control"
            description="Single-tenant deployment in your own account. No shared infrastructure, no vendor-held data, no internet dependency. Designed for restricted network environments from day one."
            highlight="Full data sovereignty"
          />
        </div>
      </section>

      {/* Feature Deep-Dives */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">
            Everything Your Team Needs
          </h2>

          <div className="space-y-16">
            <FeatureRow
              title="Sprint Tracking That Works"
              description="Define sprints with start and end dates on swimlanes. CoreFlow automatically computes stories committed, completed, and carried over. See velocity trends with trailing averages — no manual counting, no spreadsheets, no plugins."
              bullets={['Swimlane-based sprints with date ranges', 'Committed / Completed / Carried Over metrics', 'Story points support via custom fields', 'Sprint Report widget with trend lines']}
              align="left"
            />
            <FeatureRow
              title="AI Knowledge Organization"
              description="When someone writes a page, CoreFlow automatically decomposes it into semantic knowledge units, extracts concepts, and computes relationships to every other page in your knowledge base. New team members find relevant context through conceptual similarity — not just keyword search."
              bullets={['Automatic content decomposition into knowledge units', 'Cross-page relationship discovery', 'Hierarchical summaries (page, library, and global)', 'Semantic similarity search across all content']}
              align="right"
            />
            <FeatureRow
              title="Notifications Your Way"
              description="Email, Teams, or in-app — configured per user, per event type. Board owners can customize the email templates with live preview. Your ops team gets terse alerts; your product team gets friendly messages. Everyone controls what they receive."
              bullets={['Email notifications via Microsoft Graph', 'Per-board customizable message templates', 'Per-user channel and event preferences', 'Automatic due-date reminders before deadlines']}
              align="left"
            />
            <FeatureRow
              title="Advanced Search"
              description="Find any card — even archived ones. Filter by list, swimlane, label, assignee, or due date range. Results show where the match occurred with contextual snippets so you can identify the right card instantly."
              bullets={['Search across active and archived cards', 'Multi-filter with list, swimlane, label, assignee, date', 'Match context showing where the hit was found', 'Keyboard shortcut for instant access']}
              align="right"
            />
            <FeatureRow
              title="Jira-Compatible Export"
              description="Export any board as a self-contained archive that can be imported directly into Jira — including instances with no internet access. Attachments, comments, subtasks, labels, and custom fields are all preserved."
              bullets={['Standard CSV format for Jira import wizard', 'File attachments bundled for offline import', 'Checklists converted to Jira subtasks', 'Step-by-step import guide included']}
              align="left"
            />
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Security Without Compromise
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto">
            Built from day one for federal compliance requirements. Not retrofitted — designed.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-left">
            <ComplianceBadge label="Data Sovereignty" detail="Single-tenant, customer-controlled deployment" />
            <ComplianceBadge label="Encryption" detail="TLS 1.2+ in transit, encrypted at rest" />
            <ComplianceBadge label="WCAG 2.1 AA" detail="Section 508 compliant, full keyboard navigation" />
            <ComplianceBadge label="SSO / PIV / CAC" detail="SAML + OIDC identity federation" />
            <ComplianceBadge label="SBOM" detail="Software bill of materials on every release" />
            <ComplianceBadge label="SAST" detail="Static analysis with OWASP Top 10 coverage" />
            <ComplianceBadge label="Federal Experience" detail="28 years delivering technology for government" />
            <ComplianceBadge label="No External CDN" detail="All assets served locally — no third-party calls" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <a
            href="https://www.devtechnology.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            A Core Solution by <span className="font-semibold text-foreground">Dev Technology Group</span>
          </a>
          <span className="text-xs">© {new Date().getFullYear()} Dev Technology Group, Inc.</span>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product Section Sub-Components
// ---------------------------------------------------------------------------

function DifferentiatorCard({ title, description, highlight }: { title: string; description: string; highlight: string }): React.ReactElement {
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <span className="inline-block text-[11px] font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded mb-3">
        {highlight}
      </span>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function FeatureRow({ title, description, bullets, align }: { title: string; description: string; bullets: string[]; align: 'left' | 'right' }): React.ReactElement {
  return (
    <div className={`flex flex-col md:flex-row gap-8 items-start ${align === 'right' ? 'md:flex-row-reverse' : ''}`}>
      <div className="flex-1">
        <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{description}</p>
        <ul className="space-y-1.5">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-foreground">
              <CheckCircleIcon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex-1 rounded-xl bg-muted/50 border border-border h-48 flex items-center justify-center">
        <span className="text-xs text-muted-foreground">[Feature illustration]</span>
      </div>
    </div>
  );
}

function ComplianceBadge({ label, detail }: { label: string; detail: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{detail}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG Icons (no external CDN dependencies)
// ---------------------------------------------------------------------------

function CoreFlowLogo({ className }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <rect x="2" y="2" width="28" height="28" rx="6" fill="currentColor" fillOpacity="0.15" />
      <path
        d="M8 10h6v4H8zM8 18h6v4H8zM18 10h6v4h-6zM18 14h6v4h-6z"
        fill="currentColor"
        fillOpacity="0.9"
      />
      <path d="M14 12h4M14 20h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function KanbanIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="3" width="5" height="18" rx="1" />
      <rect x="10" y="3" width="5" height="12" rx="1" />
      <rect x="17" y="3" width="5" height="15" rx="1" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function TeamIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function ShieldCheckIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
